import { AgentMemberDetail, AgentMemberRow, DeviceMemberDetail, DeviceMemberRow } from '../Types/Types'
import { EMPTY_STRING } from '../../Common/Constants/Separators'
import { ArrayUtils } from '../../../NonReact/utils/ArrayUtils'
import { IsStringEmpty } from '../../../NonReact/utils/GlobalFunctions'

/**
 * Maps a device-member response payload into stable table rows, falling back to identifier-only rows when older API
 * responses do not include the richer detail collection yet.
 *
 * @param memberDetails - Rich device-member detail objects when present.
 * @param memberIdentifiers - Legacy identifier-only collection used as a fallback.
 * @returns DataTable rows for device members.
 */
export const mapDeviceMemberRows = (
    memberDetails?: Nullable<DeviceMemberDetail[]>,
    memberIdentifiers?: Nullable<string[]>
): DeviceMemberRow[] => {
    const resolvedMemberDetails: DeviceMemberDetail[] = memberDetails ?? []

    if (!ArrayUtils.isEmpty(resolvedMemberDetails)) {
        return resolvedMemberDetails
            .filter((memberDetail: DeviceMemberDetail): boolean => !IsStringEmpty(memberDetail.DeviceNumber))
            .map(
                (memberDetail: DeviceMemberDetail): DeviceMemberRow => ({
                    key: memberDetail.DeviceNumber,
                    DeviceNumber: memberDetail.DeviceNumber,
                    Description: memberDetail.Description ?? EMPTY_STRING
                })
            )
    }

    return (memberIdentifiers ?? [])
        .filter((memberIdentifier: string): boolean => !IsStringEmpty(memberIdentifier))
        .map(
            (memberIdentifier: string): DeviceMemberRow => ({
                key: memberIdentifier,
                DeviceNumber: memberIdentifier,
                Description: EMPTY_STRING
            })
        )
}

/**
 * Maps an ACD-agent-member response payload into stable table rows, falling back to identifier-only rows when older
 * API responses do not include the richer detail collection yet.
 *
 * @param memberDetails - Rich agent-member detail objects when present.
 * @param memberIdentifiers - Legacy identifier-only collection used as a fallback.
 * @returns DataTable rows for agent members.
 */
export const mapAgentMemberRows = (
    memberDetails?: Nullable<AgentMemberDetail[]>,
    memberIdentifiers?: Nullable<string[]>
): AgentMemberRow[] => {
    const resolvedMemberDetails: AgentMemberDetail[] = memberDetails ?? []

    if (!ArrayUtils.isEmpty(resolvedMemberDetails)) {
        return resolvedMemberDetails
            .filter((memberDetail: AgentMemberDetail): boolean => !IsStringEmpty(memberDetail.AgentNumber))
            .map(
                (memberDetail: AgentMemberDetail): AgentMemberRow => ({
                    key: memberDetail.AgentNumber,
                    AgentNumber: memberDetail.AgentNumber,
                    Description: memberDetail.Description ?? EMPTY_STRING
                })
            )
    }

    return (memberIdentifiers ?? [])
        .filter((memberIdentifier: string): boolean => !IsStringEmpty(memberIdentifier))
        .map(
            (memberIdentifier: string): AgentMemberRow => ({
                key: memberIdentifier,
                AgentNumber: memberIdentifier,
                Description: EMPTY_STRING
            })
        )
}
